import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HeartHandshake, ListTree, ClipboardCheck, FolderHeart, FolderLock, CreditCard, MessageSquare, Users, UserRound, LifeBuoy, ShieldCheck, LogOut, ChevronRight, Accessibility } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AppHeader } from "../../components/native/AppShell";
import { useAuth } from "../../context/AuthContext";

function HubRow({ icon: Icon, label, sub, to, onClick, testid, danger }) {
  const inner = (
    <div className="flex items-center gap-3 p-4 transition-colors active:bg-emerald-50/60" data-testid={testid}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${danger ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-900"}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold ${danger ? "text-red-700" : "text-stone-800"}`}>{label}</p>
        {sub && <p className="text-xs text-stone-400">{sub}</p>}
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />
    </div>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  return <button onClick={onClick} className="w-full text-left">{inner}</button>;
}

export function FamilyCareHub() {
  const [cm, setCm] = useState(null);
  useEffect(() => { api.get("/family/overview").then((r) => setCm(r.data.care_manager || null)).catch(() => {}); }, []);
  return (
    <div data-testid="family-care-hub">
      <AppHeader title="Care" subtitle="Your care team and records" />
      <main className="mx-auto max-w-lg px-4 py-5">
        {cm?.name && (
          <div className="p1-card mb-4 flex items-center gap-3 p-4" data-testid="care-hub-cm">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-900 text-lg font-bold text-white">{cm.name[0]}</div>
            <div>
              <p className="text-sm font-bold text-stone-900">{cm.name}</p>
              <p className="text-xs text-stone-500">Your Care Manager{(cm.zones || []).length ? ` · ${cm.zones.join(", ")}` : ""}</p>
            </div>
          </div>
        )}
        <div className="p1-card divide-y divide-stone-100">
          <HubRow icon={ListTree} label="Care Timeline" sub="Everything that happened, in order" to="/app/family/timeline" testid="care-row-timeline" />
          <HubRow icon={ClipboardCheck} label="Visit Reports" sub="Notes and vitals after every visit" to="/app/family/visits" testid="care-row-visits" />
          <HubRow icon={FolderHeart} label="Care File" sub="Medical profile, conditions, medicines" to="/app/family/care-file" testid="care-row-carefile" />
        </div>
      </main>
    </div>
  );
}

export function FamilyMore() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div data-testid="family-more">
      <AppHeader title="More" subtitle={user?.email} />
      <main className="mx-auto max-w-lg space-y-4 px-4 py-5">
        <div className="p1-card divide-y divide-stone-100">
          <HubRow icon={FolderHeart} label="Family & Care Details" sub="Care file, medical profile, preferences" to="/app/family/care-file" testid="more-row-carefile" />
          <HubRow icon={FolderLock} label="Documents" sub="Prescriptions, reports, files" to="/app/family/documents" testid="more-row-documents" />
          <HubRow icon={CreditCard} label="Plan & Billing" sub="Subscription, invoices, payments" to="/app/family/billing" testid="more-row-billing" />
          <HubRow icon={MessageSquare} label="Communication" sub="WhatsApp, alerts, preferences" to="/app/family/communication" testid="more-row-communication" />
          <HubRow icon={Users} label="Family Members" sub="Who can see and manage care" to="/app/family/members" testid="more-row-members" />
          <HubRow icon={UserRound} label="My Profile & Alerts" sub="Mobile number, alert channels" to="/app/family/profile" testid="more-row-profile" />
          <HubRow icon={Accessibility} label="Senior Mode" sub="Simple large-button view for parents" onClick={() => navigate("/app/senior")} testid="more-row-senior" />
        </div>
        <div className="p1-card divide-y divide-stone-100">
          <HubRow icon={LifeBuoy} label="Support" sub="ops@parentonecare.com · +91 86905 45601" onClick={() => { window.location.href = "mailto:ops@parentonecare.com"; }} testid="more-row-support" />
          <HubRow icon={ShieldCheck} label="Privacy Policy" to="/privacy" testid="more-row-privacy" />
          <HubRow icon={ShieldCheck} label="Terms of Service" to="/terms" testid="more-row-terms" />
        </div>
        <div className="p1-card">
          <HubRow icon={LogOut} label="Log out" danger onClick={async () => { try { await logout(); } catch { /* session may already be gone */ } window.location.assign("/login"); }} testid="more-row-logout" />
        </div>
        <p className="text-center text-[11px] text-stone-400">ParentOne Care · app v1.0</p>
      </main>
    </div>
  );
}
